import { styled } from "styled-components";

import { Alert, Radio, Spacer, Text } from "@vericus/cadmus-ui";

import { LtiService, TaskFormat } from "@/generated/graphql";
import { hasMoodlePluginAgs } from "@/graphql/helpers";

import { SettingInfoCard } from "../setting-info-card";

interface Props {
  gradingService: string;
  format: TaskFormat;
  setGradingService: (service: string) => void;
  hasMoodleGrader?: boolean;
  ltiServices?: LtiService[];
  hasTurnitinGrader?: boolean;
  hasSpeedGrader?: boolean;
  hasCadmusGrader?: boolean;
}

/**
 * Radio group selector for the Marking Tool.
 */
export function MarkingToolRadio(props: Props) {
  const {
    hasMoodleGrader = false,
    ltiServices = [],
    hasTurnitinGrader = true,
    hasSpeedGrader = false,
    hasCadmusGrader = false,
    format,
  } = props;

  const showMoodleGrader = hasMoodleGrader && hasMoodlePluginAgs(ltiServices);

  const noGrader =
    !showMoodleGrader &&
    !hasTurnitinGrader &&
    !hasSpeedGrader &&
    !hasCadmusGrader;

  return (
    <FieldSet>
      <legend>
        <Text kind="headingFour" as="span">
          Marking Tool
        </Text>
      </legend>
      <Spacer spacing={20} />
      {hasCadmusGrader && (
        <>
          <Radio
            name="gradingService"
            content="Use Cadmus Marker"
            value="cadmusgrader"
            colorVariant="lilac500"
            checked={props.gradingService === "cadmusgrader"}
            onChange={() => props.setGradingService("cadmusgrader")}
          />

          <Spacer spacing={16} />
          <SettingInfoCard showNewPill={false} backgroundColor="blue">
            <Text
              kind="bodyLine"
              style={{ margin: "0 0 8px 0", display: "inline-block" }}
            >
              {format === TaskFormat.Multiformat
                ? "Annotate and mark with rubrics, auto-mark questions with advanced moderation features, and selectively release feedback."
                : "Annotate and mark with rubrics, access advanced moderation tools for quality control, and selectively release feedback."}
            </Text>
            <Alert severity="warning" size="sm">
              You&apos;ll need to release grades and feedback manually through
              Cadmus.
            </Alert>
          </SettingInfoCard>

          <Spacer spacing={16} />
        </>
      )}
      {noGrader && (
        <Radio
          name="gradingService"
          colorVariant="lilac500"
          content="Use Turnitin Feedback Studio for marking"
          value="turnitin"
          defaultChecked={props.gradingService === "turnitin"}
          disabled
          readOnly
        />
      )}
      {hasTurnitinGrader && (
        <Radio
          name="gradingService"
          colorVariant="lilac500"
          content="Use Turnitin Feedback Studio for marking"
          value="turnitin"
          checked={props.gradingService === "turnitin"}
          onChange={() => props.setGradingService("turnitin")}
        />
      )}
      {showMoodleGrader && (
        <Radio
          name="gradingService"
          colorVariant="lilac500"
          content="Use Moodle Grader for marking"
          value="moodlegrader"
          checked={props.gradingService === "moodlegrader"}
          onChange={() => props.setGradingService("moodlegrader")}
        />
      )}
      {hasSpeedGrader && (
        <Radio
          name="gradingService"
          colorVariant="lilac500"
          content="Use Canvas SpeedGrader for marking"
          value="speedgrader"
          checked={props.gradingService === "speedgrader"}
          onChange={() => props.setGradingService("speedgrader")}
        />
      )}
    </FieldSet>
  );
}

const FieldSet = styled.fieldset`
  border: none;
  padding: 0px;
  display: flex;
  flex-direction: column;
`;
